import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Checkbox, Divider, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import type { AccountInput, NewAccount } from '@vy/types';
import { NewAccountSchema } from '@vy/types';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useAccount } from '../../../../../hooks/requests/use-account';
import { useUpdateAccount } from '../../../../../hooks/requests/use-update-account';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../utils/vy-mutation';
import { isEmptyString, isNotNullOrUndefined } from '@vy/utils';
import { IssueOnApprovalField } from './invoice-preference-tab-content-components';

export const InvoicePreferenceTabContent: React.FC = () => {
  const queryClient = useQueryClient();
  const accountQuery = useAccount();
  const updateAccountMutation = useUpdateAccount();

  const methods = useForm<AccountInput, unknown, NewAccount>({
    resolver: zodResolver(NewAccountSchema),
    defaultValues: accountQuery.data?.account,
  });

  const registerBccAddress = methods.register('settings.emailSettings.bccEmails', {
    setValueAs: (value) => value.filter(isEmptyString),
  });

  useEffect(() => {
    if (accountQuery.data?.account) {
      methods.reset(accountQuery.data.account);
    }
  }, [accountQuery.data]);

  const onSubmit = methods.handleSubmit(async (newAccount) => {
    await runMutation(
      updateAccountMutation,
      { account: newAccount },
      {
        pending: 'Updating invoice preferences...',
        success: 'Invoice preferences updated',
        error: 'Failed to update invoice preferences',
      },
    );
    await queryClient.invalidateQueries(queryKeys.account());

    methods.reset(newAccount, {
      keepDefaultValues: false,
    });
  });

  const handleCancel = () => {
    methods.reset();
  };

  return (
    <FormProvider {...methods}>
      <Stack direction="column" divider={<Divider sx={{ marginY: '20px' }} />} sx={{
        paddingY: "20px"
      }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            paddingX: "20px"
          }}>
          <Stack direction="column" sx={{
            display: "flex"
          }}>
            <Typography variant="h6">Invoice Preferences</Typography>
            <Typography variant="subtitle1" sx={{
              color: "text.secondary"
            }}>
              Manage invoice format
            </Typography>
          </Stack>

          <Stack
            direction="row"
            sx={{
              columnGap: "16px",
              alignItems: "start"
            }}>
            <Button
              variant="outlined"
              color="primary"
              sx={{
                height: '36px',
              }}
              onClick={handleCancel}
              disabled={methods.formState.isSubmitting}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={methods.formState.isSubmitting}
              onClick={onSubmit}
            >
              Save
            </Button>
          </Stack>
        </Stack>

        <Stack direction="column" spacing={2} sx={{
          paddingX: "20px"
        }}>
          <Controller
            name="settings.invoicingSettings.hideZeroAmountLineItems"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Hide zero amount line items"
              />
            )}
          />
          <Controller
            name="settings.invoicingSettings.hidePriceBreakdownInInvoicePdf"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Hide price breakdown in invoice PDF"
              />
            )}
          />
          <Controller
            name="settings.invoicingSettings.showConsumptionBreakdown"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Show consumption breakdown - of related entities"
              />
            )}
          />
          <Controller
            name="settings.invoicingSettings.forceCommitmentBreakdown"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Show commitment units - billable / non billable units"
              />
            )}
          />
          <Controller
            name="settings.invoicingSettings.showPaymentWalletInfo"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Add payment wallet info on invoice"
              />
            )}
          />
          <IssueOnApprovalField control={methods.control} />

          <Controller
            name="settings.invoicingSettings.sendInvoiceOnApproval"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Send approved invoices automatically to email"
              />
            )}
          />

          <Controller
            name="settings.invoicingSettings.deductCredits"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Deduct credits from following invoice"
              />
            )}
          />

          <Controller
            name="settings.invoicingSettings.restrictPaymentsToCreditPoolBalance"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Only allow invoice payments from credit pool balance"
              />
            )}
          />

          <Controller
            name="settings.invoicingSettings.showCustomerPortalLink"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Include customer portal link in invoice email and PDF"
              />
            )}
          />
          <Controller
            name="settings.invoicingSettings.showProductVariantOnInvoice"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Show product variant on invoice"
              />
            )}
          />
          <Controller
            name="settings.invoicingSettings.creditOnPrepaidRevision"
            control={methods.control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value ?? false} />}
                label="Automatically credit customers for prepaid amounts when creating a phase"
              />
            )}
          />
        </Stack>
        <Stack
          sx={{
            maxWidth: "md",
            paddingX: "20px",
            gap: 2
          }}>
          <Typography sx={{
            paddingX: "12px"
          }}>Billing email</Typography>

          <Controller
            control={methods.control}
            name="settings.emailSettings.billingEmail"
            defaultValue={methods.formState.defaultValues?.settings?.emailSettings?.billingEmail ?? ''}
            render={({ field, fieldState }) => {
              const error = fieldState.error;

              return (
                <TextField
                  size="small"
                  type="email"
                  fullWidth
                  {...field}
                  error={isNotNullOrUndefined(error)}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Stack>

        <Stack
          sx={{
            maxWidth: "md",
            paddingX: "20px",
            gap: 2
          }}>
          <Typography sx={{
            paddingX: "12px"
          }}>Invoicing bcc address</Typography>

          <Controller
            control={methods.control}
            name={registerBccAddress.name}
            render={({ field }) => {
              const error = methods.formState.errors.settings?.emailSettings?.bccEmails?.[0];

              return (
                <TextField
                  size="small"
                  type="email"
                  fullWidth
                  {...field}
                  error={isNotNullOrUndefined(error)}
                  helperText={error?.message}
                  value={field.value?.at(0) ?? ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    const newValue = isEmptyString(value) ? [] : [value];

                    field.onChange(newValue);
                  }}
                />
              );
            }}
          />
        </Stack>
      </Stack>
    </FormProvider>
  );
};
